// src/components/Reports.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  deleteDoc,
  doc as fsDoc,
  getDoc,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { Link } from "react-router-dom";
import { db, auth } from "../firebase";

/** ---------- helpers ---------- */
const pad2 = (n) => String(n).padStart(2, "0");
const localYMD = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const toDateSafe = (tsLike) => {
  if (!tsLike) return null;
  try {
    if (typeof tsLike?.toDate === "function") return tsLike.toDate();
    const d = new Date(tsLike);
    return isNaN(d) ? null : d;
  } catch {
    return null;
  }
};

const fmtAED = (n) =>
  (Number(n) || 0).toLocaleString("en-AE", {
    style: "currency",
    currency: "AED",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // Keep both names so older snippets still work
const fmt = fmtAED;

/** treat an “expense” document as income? */
const looksIncome = (e) => {
  const t = String(e?.entryType || "").toLowerCase();
  const cat = String(e?.category || e?.account || "").toLowerCase();
  return t === "income" || /income/.test(cat);
};

export default function Reports() {
  /** ---------- who am I? (admin can delete expenses inline) ---------- */
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) return setIsAdmin(false);
      try {
        const snap = await getDoc(fsDoc(db, "allowedUsers", u.uid));
        const role = snap.exists() ? (snap.data().role || "").toLowerCase() : "";
        setIsAdmin(role === "admin");
      } catch {
        setIsAdmin(false);
      }
    });
    return () => unsub();
  }, []);

  const [notice, setNotice] = useState(null);
  const pushNotice = (msg, type = "ok") => {
    setNotice({ msg, type });
    setTimeout(() => setNotice(null), 2500);
  };

  const [confirmDel, setConfirmDel] = useState(null);

  /** ---------- live data ---------- */
  const [sales, setSales] = useState([]);
  const [stock, setStock] = useState([]);
  const [expenses, setExpenses] = useState([]);

  useEffect(() => {
    const uq1 = query(collection(db, "sales"), orderBy("timestamp", "desc"));
    const unsub1 = onSnapshot(uq1, (s) =>
      setSales(s.docs.map((d) => ({ id: d.id, ...d.data() })))
    );

    const uq2 = query(collection(db, "stock"), orderBy("updatedAt", "desc"));
    const unsub2 = onSnapshot(uq2, (s) =>
      setStock(s.docs.map((d) => ({ id: d.id, ...d.data() })))
    );

    // expenses: support date or timestamp
    const qByDate = query(collection(db, "expenses"), orderBy("date", "desc"));
    const qByTs = query(collection(db, "expenses"), orderBy("timestamp", "desc"));

    const mergeExpenses = (docs) => {
      setExpenses((prev) => {
        const map = new Map(prev.map((e) => [e.id, e]));
        docs.forEach((d) => map.set(d.id, { id: d.id, ...d.data() }));
        return Array.from(map.values());
      });
    };

    const unsub3a = onSnapshot(qByDate, (s) => mergeExpenses(s.docs));
    const unsub3b = onSnapshot(qByTs, (s) => mergeExpenses(s.docs));

    return () => {
      unsub1();
      unsub2();
      unsub3a();
      unsub3b();
    };
  }, []);

  /** ---------- date filters + presets ---------- */
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  // ---------- period bounds (used by P&L paid bills calc) ----------
const periodStart = useMemo(() => {
  if (!from) return new Date("1970-01-01T00:00:00");
  return new Date(`${from}T00:00:00`);
}, [from]);

const periodEnd = useMemo(() => {
  if (!to) return new Date("2100-01-01T23:59:59");
  return new Date(`${to}T23:59:59`);
}, [to]);

  const applyPreset = (p) => {
    const now = new Date();
    if (p === "clear") return setFrom(""), setTo("");
    if (p === "today") {
      const s = localYMD(now);
      setFrom(s);
      setTo(s);
      return;
    }
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const qStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
    const yearStart = new Date(now.getFullYear(), 0, 1);

    if (p === "month") return setFrom(localYMD(monthStart)), setTo(localYMD(now));
    if (p === "quarter") return setFrom(localYMD(qStart)), setTo(localYMD(now));
    if (p === "year") return setFrom(localYMD(yearStart)), setTo(localYMD(now));
  };

  const inRange = (dt) => {
    const d =
      toDateSafe(dt) ||
      null;
    if (!d) return false;
    if (!from && !to) return true;
    const f = from ? new Date(`${from}T00:00:00`) : new Date("1970-01-01T00:00:00");
    const t = to ? new Date(`${to}T23:59:59`) : new Date("2100-01-01T00:00:00");
    return d >= f && d <= t;
  };

  /** ---------- price/cost maps and item names ---------- */
  const skuMeta = useMemo(() => {
    const m = {};
    for (const i of stock) {
      if (!i?.sku) continue;
      m[i.sku] = {
        price: Number(i.price) || 0,
        cost: Number(i.cost) || 0,
        name: i.name || i.item || i.title || "",
        incomeAccount:
          i.incomeAccount ||
          i.glIncome ||
          i.gl?.income ||
          i.glIncomeAccount ||
          "",
        expenseAccount:
          i.expenseAccount ||
          i.glExpense ||
          i.gl?.expense ||
          i.glExpenseAccount ||
          "",
      };
    }
    return m;
  }, [stock]);

  /** ---------- filtered slices ---------- */
  const salesF = useMemo(
    () => sales.filter((s) => inRange(s.timestamp || s.date)),
    [sales, from, to]
  );

  const stockF = useMemo(
    () => stock.filter((s) => inRange(s.updatedAt || s.date)),
    [stock, from, to]
  );

  const expensesF = useMemo(
    () =>
      expenses.filter((e) =>
        inRange(e.date || e.timestamp || e.expenseDate || e.createdAt)
      ),
    [expenses, from, to]
  );

const isPayableRow = (x) => {
  const k = (x?.entryKind || x?.kind || x?.type || "").toLowerCase();
  if (k.includes("payable") || k.includes("purchase") || k.includes("bill")) return true;
  if ((x?.vendor || x?.supplier || x?.oem) && (x?.billNo || x?.invoiceNo || x?.proformaNo)) return true;
  return false;
};

const toNum = (v, fb = 0) => (Number.isFinite(Number(v)) ? Number(v) : fb);

  // split: true expenses vs incomes
  const onlyExpenses = useMemo(
  () => expensesF.filter((e) => !looksIncome(e) && !isPayableRow(e)),
  [expensesF]
);

  // NEW: income rows (to display separately)
  const onlyIncomes = useMemo(
  () => expensesF.filter((e) => looksIncome(e) && !isPayableRow(e)),
  [expensesF]
);

  /** ---------- numbers used in header ---------- */
  const totalPurchValue = stockF.reduce(
    (n, r) => n + (Number(r.cost) || 0) * (Number(r.available) || 0),
    0
  );

  const estRevenue = salesF.reduce((n, r) => {
    const meta = skuMeta[r.sku] || {};
    const unitPrice = Number(r.unitPrice ?? r.price ?? meta.price ?? 0);
    return n + unitPrice * Number(r.qty || 0);
  }, 0);

  /** ---------- Sales row calculations (shared by 3 tables) ---------- */
  const calcSaleRow = (s) => {
    const meta = skuMeta[s.sku] || {};
    const date =
      toDateSafe(s.timestamp) || toDateSafe(s.date) || null;

    const name =
      s.item ||
      s.name ||
      meta.name ||
      s.sku ||
      "-";

    const incomeAccount =
      s.incomeAccount ||
      s.income?.account ||
      meta.incomeAccount ||
      "-";

    const unitPrice = Number(s.unitPrice ?? s.price ?? meta.price ?? 0);
    const qty = Number(s.qty || 0);
    const vatPct = Number(s.vatPct ?? s.vat ?? 0);
    const vatAmount = Number(
      s.vatAmount ?? (vatPct ? (unitPrice * qty * vatPct) / 100 : 0)
    );
    const lineTotal = unitPrice * qty + vatAmount;

    const cogsUnit = Number(meta.cost ?? s.cost ?? 0);
    const cogs = cogsUnit * qty;

    return {
      id: s.id,
      date,
      name,
      incomeAccount,
      customer: s.customer || s.customerName || "-",
      salesman: s.salesman || s.salesPerson || s.agent || "-",
      qty,
      unitPrice,
      vatAmount,
      lineTotal,
      cogs,
    };
  };

  const salesRows = useMemo(() => salesF.map(calcSaleRow), [salesF, skuMeta]);

  /** ---------- Purchases by vendor rows ---------- */
  const purchByVendorRows = useMemo(() => {
    // show only rows that actually carry a value (available * cost > 0)
    return stockF
      .map((s) => {
        const d = toDateSafe(s.updatedAt) || toDateSafe(s.date) || null;
        const vendor = s.vendor || s.supplier || s.vendorName || s.supplierName || "—";
        const invoice =
          s.invoice ||
          s.invoiceNo ||
          s.po ||
          s.poNumber ||
          s.reference ||
          "—";

        const qty = Number(s.available || 0);
        const amount = qty * (Number(s.cost) || 0);

        const freight = Number(s.freight ?? s.freightAED ?? 0);
        const customs = Number(s.customs ?? s.customsAED ?? 0);
        const total = amount + freight + customs;

        return { id: s.id, date: d, vendor, invoice, amount, freight, customs, total, rawValue: amount };
      })
      .filter((r) => r.rawValue > 0) // hide zero-value rows
      .sort((a, b) => (a.date && b.date ? a.date - b.date : 0));
  }, [stockF]);
  
  /** ---------- P&L totals ---------- */
const plTotals = useMemo(() => {
  const t = salesRows.reduce(
    (acc, r) => {
      acc.revenue += r.unitPrice * r.qty; // revenue net of VAT
      acc.cogs += r.cogs;
      return acc;
    },
    { revenue: 0, cogs: 0 }
  );
  t.gross = t.revenue - t.cogs;

  // ✅ normal operating expenses (NON-payables)
  const opExp = (onlyExpenses || []).reduce((n, e) => {
    const amt = toNum(e.amount ?? e.amountAED, 0);
    const vatP = toNum(e.vatPct ?? e.vat, 0);
    const vatA = toNum(e.vatAmount, vatP ? (amt * vatP) / 100 : 0);
    const tot = toNum(e.total, amt + vatA);
    return n + tot;
  }, 0);

  // ✅ paid portion of bills/payables in this period
  const paidBills = (expensesF || [])
  .filter(isPayableRow)
  .reduce((sum, b) => sum + toNum(b.paidAED, 0), 0);

  t.expenses = opExp + paidBills;     // ✅ add bills paid
  t.paidBills = paidBills;            // optional: keep to show separately
  t.net = t.gross - t.expenses;
  return t;
}, [salesRows, onlyExpenses, expenses, periodStart, periodEnd]);

  /** ---------- admin: delete an expense ---------- */
  const deleteExpense = async (id) => {
    if (!isAdmin) return;
    setExpenses((prev) => prev.filter((x) => x.id !== id));
    try {
      await deleteDoc(fsDoc(db, "expenses", id));
      pushNotice("Expense deleted.");
    } catch (e) {
      console.error(e);
      pushNotice("Failed to delete expense.", "err");
    } finally {
      setConfirmDel(null);
    }
  };

  /** ---------- UI state ---------- */
  const [activeTab, setActiveTab] = useState("purchases");

  return (
    <>
      {/* print-scoping */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #print-scope, #print-scope * { visibility: visible; }
          #print-scope { position: absolute; inset: 0; margin: 0; padding: 0; }
        }
      `}</style>

      <div className="min-h-screen bg-white">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#0b4a73] text-white flex items-center justify-between px-4 py-3 print:hidden">
          <h1 className="text-lg font-semibold">Reports</h1>
          <div className="flex items-center gap-2">
            <button onClick={() => window.print()} className="px-3 py-1.5 rounded bg-white/10 hover:bg-white/20">
              Export PDF
            </button>
            <button
              onClick={() => {
                const root = document.getElementById("print-scope");
                if (!root) return alert("Nothing to export.");
                const tables = root.querySelectorAll("table");
                if (!tables.length) return alert("Nothing to export.");
                const rows = [];
                tables.forEach((tbl, idx) => {
                  if (idx) rows.push([]);
                  const trs = [...tbl.querySelectorAll("tr")];
                  trs.forEach((tr) => {
                    const cols = [...tr.children].map((td) =>
                      `"${td.textContent.replace(/"/g, '""').trim()}"`
                    );
                    rows.push(cols);
                  });
                });
                const csv = rows.map((r) => r.join(",")).join("\n");
                const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `AZ-${activeTab}-${new Date().toISOString().slice(0, 10)}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
              }}
              className="px-3 py-1.5 rounded bg-white/10 hover:bg-white/20"
            >
              Export Excel
            </button>
            <Link to="/dashboard" className="px-3 py-1.5 rounded bg-white text-[#0b4a73] font-medium">
              Back to Dashboard
            </Link>
          </div>
        </div>

        {/* Filters */}
        <div className="px-4 py-3 print:hidden">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-[11px] text-slate-600">FROM</label>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="border rounded px-2 py-1"
              />
            </div>
            <div>
              <label className="block text-[11px] text-slate-600">TO</label>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="border rounded px-2 py-1"
              />
            </div>
            <button
              onClick={() => setFrom(from) /* no-op, just “search” */}
              className="px-3 py-1.5 rounded border"
            >
              Search
            </button>

            <div className="flex gap-2">
              <button onClick={() => applyPreset("today")} className="px-2 py-1 border rounded">Today</button>
              <button onClick={() => applyPreset("month")} className="px-2 py-1 border rounded">This Month</button>
              <button onClick={() => applyPreset("quarter")} className="px-2 py-1 border rounded">This Quarter</button>
              <button onClick={() => applyPreset("year")} className="px-2 py-1 border rounded">This Year</button>
              <button onClick={() => applyPreset("clear")} className="px-2 py-1 border rounded">Clear</button>
            </div>

            <div className="ml-auto text-sm text-gray-700">
              <div><b>Total Purchases:</b> {fmtAED(totalPurchValue)}</div>
              <div><b>Est. Revenue:</b> {fmtAED(estRevenue)}</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-4 print:hidden">
          <div className="flex flex-wrap gap-2">
            {[
              { id: "purchases", label: "Purchases (Stock)" },
              { id: "sales", label: "Sales" },
              { id: "bySalesman", label: "Sales by Salesman" },
              { id: "byCustomer", label: "Sales by Customer" },
              { id: "purchasesByVendor", label: "Purchases by Vendor" },
              { id: "pl", label: "P&L" },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`px-3 py-1.5 rounded border ${
                  activeTab === t.id
                    ? "bg-[#0b4a73] text-white border-[#0b4a73]"
                    : "bg-white text-[#0b4a73] hover:bg-slate-50"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="px-4 py-4" id="print-scope">
          {/* Purchases (stock) */}
          {activeTab === "purchases" && (
            <div>
              <h3 className="font-semibold mb-1">Purchases (Stock)</h3>
              <div className="text-sm text-gray-600 mb-2">
                Total items added:{" "}
                <b>{stockF.reduce((n, r) => n + Number(r.available || 0), 0)}</b>
                <br />
                Total purchase value: <b>{fmtAED(totalPurchValue)}</b>
              </div>
              <div className="border rounded overflow-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-1 text-left">Date</th>
                      <th className="px-2 py-1 text-left">SKU</th>
                      <th className="px-2 py-1 text-left">Item</th>
                      <th className="px-2 py-1 text-left">Expense Account</th>
                      <th className="px-2 py-1 text-left">Vendor</th>
                      <th className="px-2 py-1 text-right">Qty</th>
                      <th className="px-2 py-1 text-right">Unit Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockF.map((s) => {
                      const d = toDateSafe(s.updatedAt) || toDateSafe(s.date);
                      const meta = skuMeta[s.sku] || {};
                      const expAcc =
                        s.expenseAccount ||
                        s.account ||
                        meta.expenseAccount ||
                        "-";
                      return (
                        <tr key={s.id} className="border-t">
                          <td className="px-2 py-1">{d ? d.toLocaleDateString() : "-"}</td>
                          <td className="px-2 py-1">{s.sku}</td>
                          <td className="px-2 py-1">{s.name || s.item || "-"}</td>
                          <td className="px-2 py-1">{expAcc}</td>
                          <td className="px-2 py-1">
                            {s.vendor || s.supplier || s.vendorName || s.supplierName || "—"}
                          </td>
                          <td className="px-2 py-1 text-right">{s.available || 0}</td>
                          <td className="px-2 py-1 text-right">
                            {(Number(s.cost || 0)).toLocaleString("en-AE", { minimumFractionDigits: 2 })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Sales (transactional) */}
          {activeTab === "sales" && (
            <div>
              <h3 className="font-semibold mb-1">Sales</h3>
              <div className="text-sm text-gray-600 mb-2">
                Total units sold: <b>{salesRows.reduce((n, r) => n + r.qty, 0)}</b>
                <br />
                Estimated revenue: <b>{fmtAED(estRevenue)}</b>
              </div>

              <div className="border rounded overflow-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-1 text-left">Date</th>
                      <th className="px-2 py-1 text-left">Item</th>
                      <th className="px-2 py-1 text-left">Income Account</th>
                      <th className="px-2 py-1 text-right">Qty</th>
                      <th className="px-2 py-1 text-right">Price</th>
                      <th className="px-2 py-1 text-right">VAT</th>
                      <th className="px-2 py-1 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salesRows.map((r) => (
                      <tr key={r.id} className="border-t">
                        <td className="px-2 py-1">{r.date ? r.date.toLocaleDateString() : "-"}</td>
                        <td className="px-2 py-1">{r.name}</td>
                        <td className="px-2 py-1">{r.incomeAccount || "-"}</td>
                        <td className="px-2 py-1 text-right">{r.qty}</td>
                        <td className="px-2 py-1 text-right">{fmtAED(r.unitPrice)}</td>
                        <td className="px-2 py-1 text-right">{fmtAED(r.vatAmount)}</td>
                        <td className="px-2 py-1 text-right">{fmtAED(r.lineTotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Sales by Salesman (transactional, includes salesman column) */}
          {activeTab === "bySalesman" && (
  <div>
    <h3 className="font-semibold mb-1">Sales by Salesman</h3>
    <div className="border rounded overflow-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-2 py-1 text-left">Date</th>
            <th className="px-2 py-1 text-left">Salesman</th>
            <th className="px-2 py-1 text-left">Item</th>
            <th className="px-2 py-1 text-left">Income Account</th>
            <th className="px-2 py-1 text-left">Customer</th>
            <th className="px-2 py-1 text-right">Qty</th>
            {/* changed columns */}
            <th className="px-2 py-1 text-right">Revenue</th>
            <th className="px-2 py-1 text-right">COGS</th>
            <th className="px-2 py-1 text-right">Profit</th>
          </tr>
        </thead>
        <tbody>
          {salesRows.map((r) => {
            const revenue = r.unitPrice * r.qty;       // net of VAT
            const profit  = revenue - r.cogs;
            return (
              <tr key={r.id} className="border-t">
                <td className="px-2 py-1">{r.date ? r.date.toLocaleDateString() : "-"}</td>
                <td className="px-2 py-1">{r.salesman}</td>
                <td className="px-2 py-1">{r.name}</td>
                <td className="px-2 py-1">{r.incomeAccount || "-"}</td>
                <td className="px-2 py-1">{r.customer}</td>
                <td className="px-2 py-1 text-right">{r.qty}</td>
                <td className="px-2 py-1 text-right">{fmtAED(revenue)}</td>
                <td className="px-2 py-1 text-right">{fmtAED(r.cogs)}</td>
                <td className="px-2 py-1 text-right">{fmtAED(profit)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
)}

          {/* Sales by Customer (transactional, includes customer first) */}
          {activeTab === "byCustomer" && (
  <div>
    <h3 className="font-semibold mb-1">Sales by Customer</h3>
    <div className="border rounded overflow-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-2 py-1 text-left">Date</th>
            <th className="px-2 py-1 text-left">Customer</th>
            <th className="px-2 py-1 text-left">Item</th>
            <th className="px-2 py-1 text-left">Income Account</th>
            <th className="px-2 py-1 text-right">Qty</th>
            <th className="px-2 py-1 text-right">Revenue</th>
            <th className="px-2 py-1 text-right">COGS</th>
            <th className="px-2 py-1 text-right">Profit</th>
          </tr>
        </thead>
        <tbody>
          {salesRows.map((r) => {
            const revenue = (Number(r.unitPrice) || 0) * (Number(r.qty) || 0); // net of VAT
            const profit  = revenue - (Number(r.cogs) || 0);
            return (
              <tr key={r.id} className="border-t">
                <td className="px-2 py-1">{r.date ? r.date.toLocaleDateString() : "-"}</td>
                <td className="px-2 py-1">{r.customer}</td>
                <td className="px-2 py-1">{r.name}</td>
                <td className="px-2 py-1">{r.incomeAccount || "-"}</td>
                <td className="px-2 py-1 text-right">{r.qty}</td>
                <td className="px-2 py-1 text-right">{fmt(revenue)}</td>
                <td className="px-2 py-1 text-right">{fmt(r.cogs)}</td>
                <td className="px-2 py-1 text-right">{fmt(profit)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
)}


          {/* Purchases by Vendor */}
          {activeTab === "purchasesByVendor" && (
            <div>
              <h3 className="font-semibold mb-1">Purchases by Vendor</h3>
              <div className="border rounded overflow-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-1 text-left">Date</th>
                      <th className="px-2 py-1 text-left">Vendor</th>
                      <th className="px-2 py-1 text-left">Invoice / PO</th>
                      <th className="px-2 py-1 text-right">Amount</th>
                      <th className="px-2 py-1 text-right">Freight</th>
                      <th className="px-2 py-1 text-right">Customs</th>
                      <th className="px-2 py-1 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchByVendorRows.map((r) => (
                      <tr key={r.id} className="border-t">
                        <td className="px-2 py-1">{r.date ? r.date.toLocaleDateString() : "-"}</td>
                        <td className="px-2 py-1">{r.vendor}</td>
                        <td className="px-2 py-1">{r.invoice}</td>
                        <td className="px-2 py-1 text-right">{fmtAED(r.amount)}</td>
                        <td className="px-2 py-1 text-right">{fmtAED(r.freight)}</td>
                        <td className="px-2 py-1 text-right">{fmtAED(r.customs)}</td>
                        <td className="px-2 py-1 text-right">{fmtAED(r.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* P&L */}
          {activeTab === "pl" && (
            <>
              <h3 className="font-semibold mb-2">P&L (Sales Income - Overall Expense)</h3>

              <div className="border rounded overflow-hidden mb-4">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left">Item</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t">
                      <td className="px-3 py-2">Revenue</td>
                      <td className="px-3 py-2 text-right">{fmtAED(plTotals.revenue)}</td>
                    </tr>
                    <tr className="border-t">
                      <td className="px-3 py-2">COGS</td>
                      <td className="px-3 py-2 text-right">{fmtAED(plTotals.cogs)}</td>
                    </tr>
                    <tr className="border-t bg-gray-50">
                      <td className="px-3 py-2 font-semibold">Gross Profit</td>
                      <td className="px-3 py-2 text-right font-semibold">{fmtAED(plTotals.gross)}</td>
                    </tr>
                    <tr className="border-t">
                      <td className="px-3 py-2">Operating Expenses</td>
                      <td className="px-3 py-2 text-right">{fmtAED(plTotals.expenses)}</td>
                    </tr>
                    <tr className="border-t bg-gray-50">
                      <td className="px-3 py-2 font-semibold">Net Profit</td>
                      <td className="px-3 py-2 text-right font-semibold">{fmtAED(plTotals.net)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Expense breakdown (exclude incomes) */}
              <h4 className="font-semibold mb-2">Expense Breakdown</h4>
              <div className="border rounded overflow-auto mb-4">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-1 text-left">Category</th>
                      <th className="px-2 py-1 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const m = new Map();
                      onlyExpenses.forEach((e) => {
                        const cat = e.category || e.account || "Other";
                        const amt = Number(e.amount ?? e.amountAED ?? 0) || 0;
                        const vatPct = Number(e.vatPct ?? e.vat ?? 0);
                        const vatAmt = Number(e.vatAmount ?? (vatPct ? (amt * vatPct) / 100 : 0));
                        const tot = Number(e.total ?? amt + vatAmt);
                        m.set(cat, (m.get(cat) || 0) + tot);
                      });
                      const rows = [...m.entries()].sort((a, b) => b[1] - a[1]);
                      if (!rows.length)
                        return (
                          <tr>
                            <td className="px-2 py-2 text-gray-500" colSpan={2}>
                              No expenses found for the selected period.
                            </td>
                          </tr>
                        );
                      return rows.map(([cat, amount]) => (
                        <tr key={cat} className="border-t">
                          <td className="px-2 py-1">{cat}</td>
                          <td className="px-2 py-1 text-right">{fmtAED(amount)}</td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>

               {/* Income breakdown (from “Other Income” etc.) */}
<h4 className="font-semibold mb-2">Income Breakdown</h4>
<div className="border rounded overflow-auto mb-4">
  <table className="min-w-full text-sm">
    <thead className="bg-gray-50">
      <tr>
        <th className="px-2 py-1 text-left">Category</th>
        <th className="px-2 py-1 text-right">Amount</th>
      </tr>
    </thead>
    <tbody>
      {(() => {
        const m = new Map();
        onlyIncomes.forEach((e) => {
          const cat = e.category || e.account || "Other Income";
          const amt = Number(e.amount ?? e.amountAED ?? 0) || 0;
          const vatP = Number(e.vatPct ?? e.vat ?? 0);
          const vatA = Number(e.vatAmount ?? (vatP ? (amt * vatP) / 100 : 0));
          const tot  = Number(e.total ?? amt + vatA);
          m.set(cat, (m.get(cat) || 0) + tot);
        });
        const rows = [...m.entries()].sort((a, b) => b[1] - a[1]);
        if (!rows.length)
          return (
            <tr>
              <td className="px-2 py-2 text-gray-500" colSpan={2}>
                No income found for the selected period.
              </td>
            </tr>
          );
        return rows.map(([cat, amount]) => (
          <tr key={cat} className="border-t">
            <td className="px-2 py-1">{cat}</td>
            <td className="px-2 py-1 text-right">{fmtAED(amount)}</td>
          </tr>
        ));
      })()}
    </tbody>
  </table>
</div>

              {/* Admin-only raw list (delete inline) */}
              {isAdmin && (
                <>
                  {notice && (
                    <div
                      className={`mb-2 px-3 py-2 rounded border text-sm bg-white ${
                        notice.type === "err"
                          ? "border-red-300 text-red-700"
                          : "border-emerald-300 text-emerald-700"
                      }`}
                    >
                      {notice.msg}
                    </div>
                  )}

                  <h4 className="font-semibold mt-4 mb-2">Expense Entries (this period)</h4>
                  <div className="border rounded overflow-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-2 py-1 text-left">Date</th>
                          <th className="px-2 py-1 text-left">Category</th>
                          <th className="px-2 py-1 text-right">Amount</th>
                          <th className="px-2 py-1 text-right">VAT</th>
                          <th className="px-2 py-1 text-right">Total</th>
                          <th className="px-2 py-1 text-left">Note</th>
                          <th className="px-2 py-1"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {onlyExpenses.map((e) => {
                          const d =
                            toDateSafe(e.date) ||
                            toDateSafe(e.timestamp) ||
                            toDateSafe(e.expenseDate) ||
                            toDateSafe(e.createdAt);
                          const amt = Number(e.amount ?? e.amountAED ?? 0) || 0;
                          const vatP = Number(e.vatPct ?? e.vat ?? 0);
                          const vatA = Number(e.vatAmount ?? (vatP ? (amt * vatP) / 100 : 0));
                          const tot = Number(e.total ?? amt + vatA) || 0;

                          return (
                            <tr key={e.id} className="border-t">
                              <td className="px-2 py-1">{d ? d.toLocaleDateString() : "-"}</td>
                              <td className="px-2 py-1">{e.category || e.account || "-"}</td>
                              <td className="px-2 py-1 text-right">
                                {amt.toLocaleString("en-AE", { minimumFractionDigits: 2 })}
                              </td>
                              <td className="px-2 py-1 text-right">
                                {vatA.toLocaleString("en-AE", { minimumFractionDigits: 2 })}
                              </td>
                              <td className="px-2 py-1 text-right">
                                {tot.toLocaleString("en-AE", { minimumFractionDigits: 2 })}
                              </td>
                              <td className="px-2 py-1">{e.note || e.notes || ""}</td>
                              <td className="px-2 py-1 text-right">
                                {confirmDel === e.id ? (
                                  <span className="inline-flex gap-2">
                                    <button
                                      onClick={() => deleteExpense(e.id)}
                                      className="px-2 py-0.5 rounded border bg-white hover:bg-emerald-50"
                                    >
                                      Confirm
                                    </button>
                                    <button
                                      onClick={() => setConfirmDel(null)}
                                      className="px-2 py-0.5 rounded border bg-white hover:bg-slate-50"
                                    >
                                      Cancel
                                    </button>
                                  </span>
                                ) : (
                                  <button
                                    onClick={() => setConfirmDel(e.id)}
                                    className="px-2 py-0.5 rounded border hover:bg-red-50 text-red-700"
                                  >
                                    Delete
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                        {!onlyExpenses.length && (
                          <tr>
                            <td className="px-2 py-2 text-gray-500" colSpan={7}>
                              No expense entries.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
